import { useUiStore } from '@/lib/store/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Eye, Minus, Plus } from 'lucide-react';
import { useEffect } from 'react';

const FONT_LABELS: Record<number, string> = {
  '-3': 'Smaller-3',
  '-2': 'Smaller-2',
  '-1': 'Smaller-1',
  '0': 'Default',
  '1': 'Larger+1',
  '2': 'Larger+2',
  '3': 'Larger+3',
  '4': 'Larger+4',
  '5': 'Larger+5',
  '6': 'Larger+6',
};
const FONT_OFFSETS = [-3, -2, -1, 0, 1, 2, 3, 4, 5, 6];
const ICON_OFFSETS = [-3, -2, -1, 0, 1, 2, 3];

export function ViewMenu() {
  const fontStep = useUiStore((s) => s.fontStep);
  const bumpFont = useUiStore((s) => s.bumpFont);
  const resetFont = useUiStore((s) => s.resetFont);
  const iconDrone = useUiStore((s) => s.iconSizeDrone);
  const iconAircraft = useUiStore((s) => s.iconSizeAircraft);
  const bumpIcon = useUiStore((s) => s.bumpIcon);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  // Apply font step to body. Base 15px (raised from 13px) for default
  // readability; the stepper adjusts from here. Keep in sync with index.css.
  useEffect(() => {
    const px = 15 + fontStep;
    document.body.style.fontSize = `${px}px`;
  }, [fontStep]);

  // Apply theme to html
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Eye size={13} />
          <span className="hidden lg:inline">View</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-3">
        <DropdownMenuLabel className="px-0">Font Size</DropdownMenuLabel>
        <div className="flex items-center gap-2 mb-2">
          <Button
            size="xs"
            variant="outline"
            disabled={fontStep === FONT_OFFSETS[0]}
            onClick={() => bumpFont(-1)}
          >
            <Minus size={10} /> A
          </Button>
          <span className="flex-1 text-center text-[0.769em] font-mono">{FONT_LABELS[fontStep]}</span>
          <Button
            size="xs"
            variant="outline"
            disabled={fontStep === FONT_OFFSETS[FONT_OFFSETS.length - 1]}
            onClick={() => bumpFont(1)}
          >
            A <Plus size={10} />
          </Button>
          <Button size="xs" variant="ghost" disabled={fontStep === 0} onClick={resetFont}>
            Reset
          </Button>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="px-0 mt-1">Icon sizes</DropdownMenuLabel>
        <IconStepper label="Aircraft" value={iconAircraft} onChange={(d) => bumpIcon('aircraft', d)} />
        <IconStepper label="Drone" value={iconDrone} onChange={(d) => bumpIcon('drone', d)} />
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="px-0 mt-1">Colour Mode</DropdownMenuLabel>
        <div className="flex gap-1.5">
          <Button
            size="xs"
            variant="toggle"
            data-on={theme === 'light'}
            onClick={() => setTheme('light')}
            className="flex-1"
          >
            ☀ Light
          </Button>
          <Button
            size="xs"
            variant="toggle"
            data-on={theme === 'dark'}
            onClick={() => setTheme('dark')}
            className="flex-1"
          >
            ☾ Dark
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function IconStepper({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (d: number) => void;
}) {
  return (
    <div className="flex items-center gap-2 my-1">
      <span className="text-[0.769em] font-semibold w-16">{label}</span>
      <Button size="xs" variant="outline" disabled={value === ICON_OFFSETS[0]} onClick={() => onChange(-1)}>
        −
      </Button>
      <span className="flex-1 text-center text-[0.692em] font-mono">{value === 0 ? 'Default' : value > 0 ? `+${value}` : value}</span>
      <Button size="xs" variant="outline" disabled={value === ICON_OFFSETS[ICON_OFFSETS.length - 1]} onClick={() => onChange(1)}>
        +
      </Button>
    </div>
  );
}
